// src/components/OrderEntry/panels/PickingSheetsPanel.jsx
// =============================================================================
// PICKING SHEETS PANEL
// =============================================================================

import React from 'react';

import CloseIcon from '@mui/icons-material/Close';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// =============================================================================
// BLOCK PICKING.SHEETS.PROPS.0 — Props (Badge-6 is ONLY submit/copy-all)
// =============================================================================
export default function PickingSheetsPanel({
  sheets,
  onClearAll,

  // Optional: delete actions (OrderEntry passes these)
  onRemoveLine,
  onRemoveSheet,

  // Sheet-specific only (no Sugar open, no submit gating)
  onCopyNotes,

  // Used to disable UI while a sheet is submitting (submission lives in Badge 6)
  submitBusyBySheetId,
}) {
  const theme = useTheme();

  if (!Array.isArray(sheets) || sheets.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Picking Sheets
        </Typography>
        <Button size="small" variant="outlined" onClick={onClearAll}>
          Clear All
        </Button>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {sheets.map((s) => {
          const busy = Boolean(submitBusyBySheetId?.[s.sheetId]);
          const h = s.header || {};

          // ============================================================================
          // BLOCK PICKING.SHEETS.STYLES.0 — Visual status mapping (CARD-LEVEL SCOPE)
          // ============================================================================
          const flags = h.flags || {};

          const isBackOrder = Boolean(flags.backOrder || flags.specOrderBo);

          // Treat any non-BO special-order type as "SPECIAL / MTO" for card styling
          const isSpecialOrMto = Boolean(
            flags.specialOrder ||
            flags.madeToOrder ||
            flags.specOrderMo ||
            flags.specOrderDs ||
            flags.specOrderBl
          );
          const isGround = typeof h.shipVia === 'string' && /GD|GND|GROUND/i.test(h.shipVia);

          // Card outline color
          let cardBorderColor = 'rgba(0,0,0,0.12)';
          if (isBackOrder) {
            cardBorderColor = '#d32f2f'; // red
          } else if (isSpecialOrMto) {
            cardBorderColor = '#1976d2'; // blue
          }

          // Header background color (theme-aware for dark mode)
          let headerBgColor = theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5'; // ground default
          if (!isGround) {
            headerBgColor = theme.palette.mode === 'dark' ? '#5d4037' : '#fdecea'; // non-ground
          }

          return (
            <Card
              key={s.sheetId}
              variant="outlined"
              sx={{
                width: 420,
                borderRadius: 2,
                borderWidth: 2,
                borderColor: cardBorderColor,
              }}
            >
              <CardContent sx={{ p: 1.25 }}>
                {/* =============================================================================
                  BLOCK PICKING.SHEETS.CARDBODY.0 — Header + Lines rendering (minimal, current-file-safe)
                  ----------------------------------------------------------------------------
                  Uses only:
                  - s.pickSheetNo
                  - s.header
                  - s.lines
                  No remove-line/remove-sheet controls (Badge 6 owns workflow actions).
                ============================================================================= */}
                {(() => {
                  const lines = Array.isArray(s.lines) ? s.lines : [];

                  const headerLine1 = [h.masId, h.shipId].filter(Boolean).join(' / ');
                  const headerLine2 = [
                    h.shipVia,
                    h.frtPay === 'FC' && h.frtAccount ? `FC – ${h.frtAccount}` : h.frtPay,
                    h.termsCode ? `Terms: ${h.termsCode}` : '',
                  ]
                    .filter(Boolean)
                    .join(' • ');

                  const headerLine3 = [
                    h.contractCode ? `Contract: ${h.contractCode}` : '',
                    h.etaLabel ? `ETA: ${h.etaLabel}` : '',
                  ]
                    .filter(Boolean)
                    .join(' • ');

                  return (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          mb: 0.75,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: headerBgColor,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          Sheet #{s.pickSheetNo || 1}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isBackOrder && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#d32f2f', fontWeight: 700 }}
                            >
                              BACKORDER
                            </Typography>
                          )}

                          {!isBackOrder && isSpecialOrMto && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#1976d2', fontWeight: 700 }}
                            >
                              SPECIAL / MTO
                            </Typography>
                          )}

                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {headerLine1 || '—'}
                          </Typography>

                          {typeof onRemoveSheet === 'function' ? (
                            <IconButton
                              size="small"
                              aria-label="Remove sheet"
                              onClick={() => onRemoveSheet(s.sheetId)}
                              disabled={busy}
                              sx={{ ml: 0.5 }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </Box>

                      <Box sx={{ mb: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {headerLine2 || '—'}
                        </Typography>

                        {headerLine3 ? (
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                            {headerLine3}
                          </Typography>
                        ) : null}

                        {h.flags && Object.keys(h.flags).length > 0 ? (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', opacity: 0.8, width: '100%' }}
                          >
                            Flags:
                            {h.flags.zeroInsurance ? ' ZI' : ''}
                            {h.flags.specOrderBo ? ' BO' : ''}
                            {h.flags.specOrderMo ? ' MO' : ''}
                            {h.flags.specOrderDs ? ' DS' : ''}
                            {h.flags.specOrderBl ? ' BL' : ''}
                            {h.flags.splitCommission ? ' SPLIT' : ''}
                            {h.flags.houseAccount ? ' HOUSE' : ''}
                            {h.flags.internationalSale ? ' INTL' : ''}
                          </Typography>
                        ) : null}
                      </Box>

                      <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', pt: 0.75 }}>
                        {lines.length === 0 ? (
                          <Typography variant="caption" sx={{ opacity: 0.75 }}>
                            No lines on this sheet yet.
                          </Typography>
                        ) : (
                          lines.map((l, idx) => {
                            const partNo = l.partNo || '—';
                            const qtyNum = Number(l.qty);
                            const qty = Number.isFinite(qtyNum) ? qtyNum : (l.qty ?? '—');
                            const uom = l.uom || '';

                            const priceNum = Number(l.price);
                            const hasPrice = Number.isFinite(priceNum);

                            const fmtMoney = (n) =>
                              Number.isFinite(n)
                                ? n.toLocaleString(undefined, {
                                    style: 'currency',
                                    currency: 'USD',
                                  })
                                : '';

                            const priceText = hasPrice ? fmtMoney(priceNum) : '';
                            const lineTotalText =
                              hasPrice && Number.isFinite(Number(qtyNum))
                                ? fmtMoney(priceNum * qtyNum)
                                : '';

                            const borderStyle =
                              idx === lines.length - 1 ? 'none' : '1px dashed rgba(0,0,0,0.12)';

                            return (
                              <Box
                                key={l.lineId || `${s.sheetId}-${idx}`}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(0, 1fr) 80px 90px 100px 36px',
                                  gap: 1,
                                  alignItems: 'center',
                                  py: 0.5,
                                  borderBottom: borderStyle,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    whiteSpace: 'normal',
                                    wordBreak: 'keep-all',
                                    overflowWrap: 'anywhere',
                                  }}
                                >
                                  {partNo}
                                </Typography>

                                <Typography variant="body2" sx={{ textAlign: 'right' }}>
                                  {qty} {uom ? `(${uom})` : ''}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', textAlign: 'right' }}
                                >
                                  {priceText}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ fontFamily: 'monospace', textAlign: 'right' }}
                                >
                                  {lineTotalText}
                                </Typography>

                                {typeof onRemoveLine === 'function' ? (
                                  <IconButton
                                    size="small"
                                    aria-label="Remove line"
                                    onClick={() => onRemoveLine(s.sheetId, l.lineId)}
                                    disabled={busy}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                ) : (
                                  <Box />
                                )}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    </>
                  );
                })()}

                {/* =============================================================================
                    BLOCK PICKING.SHEETS.ACTIONS.0 — Per-sheet Copy Notes ONLY
                    ----------------------------------------------------------------------------
                    - "Copy Notes (This Sheet)" stays here
                    - Submit is removed (Badge 6 only)
                   ============================================================================= */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    mt: 0.75,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onCopyNotes?.(s)}
                    disabled={busy}
                  >
                    Copy Notes (This Sheet)
                  </Button>

                  <Box />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
